import { DateSelectionDropdownOption } from '../components/Dropdown/DateSelectionDropdown'
import { getLocalizedDateFilter, TranslationKey } from '../translations/default'
import { TFn } from '../translations/translations'
import { formatDate } from '../utils/misc'

export type FilterMap<T = {}> = Partial<Record<keyof T, string | undefined>> & {
  page?: string
}

/**
 * Maps the filter name to the actual API query that gets passed to the API request
 * @param filters
 * @param queryMap - Map of shape {[key: FilterName]: value: APIQuery}
 */
export const mapFiltersToAPIQuery = <T = undefined>(
  filters: FilterMap<T>,
  queryMap: Record<string, string>,
) =>
  Object.keys(filters).reduce<Record<string, string | undefined>>(
    (obj, key) => {
      if (queryMap.hasOwnProperty(key)) {
        obj[queryMap[key]] = filters[key as keyof T]
      }
      return obj
    },
    {},
  )

export const countActiveFilters = (
  filters: FilterMap | undefined,
  regexFilter?: RegExp,
) =>
  filters &&
  Object.keys(filters).reduce(
    (count, key) =>
      filters[key as keyof FilterMap] !== undefined &&
      (regexFilter === undefined || !regexFilter.test(key))
        ? ++count
        : count,
    0,
  )

/**
 * Maps and validates the supplies filters object based on the filterKeyToAPIMap record.
 * eg. a map of
  {
   customerName: 'filter[customerName.icontains]'
  }
 * would map customerName to filter[customerName.icontains] and would drop any other filters that don't match
 */
export const mapAndValidateFilters = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filters: any | undefined,
  filterKeyToAPImap: Record<string, string>,
) => {
  return filters !== undefined
    ? Object.keys(filters).reduce((obj: Record<string, string>, filterKey) => {
        const filterValue = filters[filterKey]
        if (filterValue !== undefined) {
          const filterId = filterKeyToAPImap[filterKey]
          obj[filterId] = Array.isArray(filterValue)
            ? filterValue.join(',')
            : filterValue
        }
        return obj
      }, {})
    : {}
}

export const dateFilterToAPIQuery = (date: number | string | undefined) =>
  date !== undefined ? new Date(date).toISOString() : undefined

const subtractDaysFromDate = (days: number, date: Date = new Date()) => {
  const d = new Date(date.getTime())
  d.setHours(0, 0, 0, 0)

  return new Date(d.setDate(d.getDate() - days))
}

export const generateDefaultQuickRange = (
  t: TFn,
): DateSelectionDropdownOption[] => {
  const currentDate = new Date()
  currentDate.setHours(23, 59, 59, 0)
  return [
    {
      description: getLocalizedDateFilter(t, 'lastWeek'),
      end: currentDate,
      start: subtractDaysFromDate(7, currentDate),
      value: getLocalizedDateFilter(t, 'lastWeek'),
    },
    {
      description: getLocalizedDateFilter(t, 'lastMonth'),
      end: currentDate,
      start: subtractDaysFromDate(30, currentDate),
      value: getLocalizedDateFilter(t, 'lastMonth'),
    },
    {
      description: getLocalizedDateFilter(t, 'last3Months'),
      end: currentDate,
      start: subtractDaysFromDate(90, currentDate),
      value: getLocalizedDateFilter(t, 'last3Months'),
    },
    {
      description: getLocalizedDateFilter(t, 'lastYear'),
      end: currentDate,
      start: subtractDaysFromDate(365, currentDate),
      value: getLocalizedDateFilter(t, 'lastYear'),
    },
  ]
}

const formatISOString = (date: string | undefined) =>
  date && formatDate(new Date(date), '/')

export const dateRangeToPillLabel = (
  t: TFn,
  filters: Record<string, string | undefined>,
  filterKey: string,
) => {
  const start = formatISOString(filters[`${filterKey}GreaterEqual`])
  const end = formatISOString(filters[`${filterKey}LessEqual`])
  return `${t(`filter.pill.${filterKey}` as TranslationKey)} ${
    start ? `${t('filter.from')} ${start}` : ''
  } ${end ? `${t('filter.until')} ${end}` : ''}`
}

export const pickOnlyOneFromDateRange = (
  key: string,
  filters: Record<string, string | undefined>,
  dateRanges: string,
) => {
  const result = key.match(new RegExp(`(${dateRanges})GreaterEqual`))
  // Returns true if date already exists as LessEqual (to prevent two pills from showing)
  return !(
    result &&
    result.length &&
    filters[`${result[1]}LessEqual`] !== undefined
  )
}
